import _CharacterSelectSetDefaultMutation from "../__generated__/CharacterSelectSetDefaultMutation.graphql";
import _CharacterSelectPreviewMutation from "../__generated__/CharacterSelectPreviewMutation.graphql";
import _CharacterSelectQuery from "../__generated__/CharacterSelectQuery.graphql";
import { Check, Loader2, Volume2 } from 'lucide-react';
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import type { CharacterSelectPreviewMutation } from '../__generated__/CharacterSelectPreviewMutation.graphql';
import type { CharacterSelectQuery } from '../__generated__/CharacterSelectQuery.graphql';
import type { CharacterSelectSetDefaultMutation } from '../__generated__/CharacterSelectSetDefaultMutation.graphql';
import { CharacterFace, usePhase } from '../avatar/CharacterFace';
import { clock } from '../avatar/clock';
import { celebrationWait } from '../character/choose';
import { t, type PlainKey } from '../i18n/t';
import { expressionForCard, previewReducer, type PreviewState } from '../character/preview';

/**
 * Who the learner practises with.
 *
 * The cast is readable without signing in, so this screen works before an
 * account exists; `onChosen` is what the caller does with the answer — save it
 * against the learner, or carry it into `startSession`.
 */
const Cast = (_CharacterSelectQuery.hash && _CharacterSelectQuery.hash !== "4bdc7c76336cfa3ad45254c86a98b297" && console.error("The definition of 'CharacterSelectQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _CharacterSelectQuery);
const Preview = (_CharacterSelectPreviewMutation.hash && _CharacterSelectPreviewMutation.hash !== "d704500d7710eb739ee19dd7da3961ea" && console.error("The definition of 'CharacterSelectPreviewMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _CharacterSelectPreviewMutation);
const SetDefault = (_CharacterSelectSetDefaultMutation.hash && _CharacterSelectSetDefaultMutation.hash !== "1aebe4c1ce5c2467f0ef73fb43a1d47e" && console.error("The definition of 'CharacterSelectSetDefaultMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _CharacterSelectSetDefaultMutation);
const IDLE: PreviewState = {
  playing: null,
  loading: null
};

/** What each `pace` reads as. The server sends the word; this is its gloss. */
const PACE_LABEL: Partial<Record<string, PlainKey>> = {
  brisk: 'character-pace-brisk',
  even: 'character-pace-even',
  slow: 'character-pace-slow'
};
function paceLabel(pace: string): string {
  const key = PACE_LABEL[pace];
  return key ? t(key) : pace;
}
export function CharacterSelect({
  chosen = null,
  onChosen,
  embedded = false
}: {
  chosen?: string | null;
  onChosen?: () => void;
  /** Inside Account's tabs, which already give the page its gutter and its title. */
  embedded?: boolean;
}) {
  const navigate = useNavigate();
  // Chosen, so `/` stops redirecting here. Going home rather than calling a
  // refetch callback: the route decides where next, not this screen.
  const done = onChosen ?? (() => navigate('/', {
    replace: true
  }));
  const data = useLazyLoadQuery<CharacterSelectQuery>(Cast, {});
  const [preview, dispatch] = useReducer(previewReducer, IDLE);
  const [previewVoice] = useMutation<CharacterSelectPreviewMutation>(Preview);
  const [setDefault, choosing] = useMutation<CharacterSelectSetDefaultMutation>(SetDefault);
  // Which card is mid-save, so a slow network shows "Saving…" instead of a
  // button that looks dead; cleared on failure so the learner can retry.
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [chooseFailed, setChooseFailed] = useState(false);
  // Which face is celebrating the pick, from the press until the screen
  // leaves or the write fails (first-impressions design §5, §7).
  const [celebrating, setCelebrating] = useState<string | null>(null);
  const leaving = useRef<number | null>(null);
  // From the press until the screen leaves or the write fails. Relay's
  // `choosing` clears when the write lands, inside the beat, and a second tap
  // there started a second save whose timer later navigated from wherever the
  // learner had got to -- tearing down a session already started.
  const busy = useRef(false);
  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
    if (leaving.current !== null) window.clearTimeout(leaving.current);
  }, []);
  // One element for the whole screen: the reducer already guarantees one voice
  // at a time, and a per-card element would make that guarantee twice, in two
  // places, with only one of them tested.
  const audio = useRef<HTMLAudioElement | null>(null);
  // One subscription for the whole grid rather than one per card: the clock is
  // a single loop either way, but nine subscribers would be nine React
  // re-renders a frame where one does.
  const phase = usePhase();
  const play = useCallback((id: string) => {
    audio.current?.pause();
    const wasPlaying = preview.playing === id;
    dispatch({
      type: 'play',
      id
    });
    if (wasPlaying) return;
    previewVoice({
      variables: {
        id
      },
      onCompleted: ({
        previewVoice: result
      }) => {
        const wav = result.wavBase64;
        if (!wav) {
          dispatch({
            type: 'failed',
            id
          });
          return;
        }
        dispatch({
          type: 'ready',
          id
        });
        const el = new Audio(`data:audio/wav;base64,${wav}`);
        el.onended = () => dispatch({
          type: 'ended'
        });
        audio.current = el;
        void el.play().catch(() => dispatch({
          type: 'failed',
          id
        }));
      },
      onError: () => dispatch({
        type: 'failed',
        id
      })
    });
  }, [preview.playing, previewVoice]);
  const choose = useCallback((id: string) => {
    // `done()` waits for `onCompleted` rather than firing alongside the
    // mutation: `RequireCharacter` re-reads `me` with `network-only` the
    // moment `/` mounts, and that read raced this mutation's write when the
    // two fired together -- if the read won, the learner was bounced back
    // to `/character` and had to pick twice.
    if (busy.current) return;
    busy.current = true;
    setChooseFailed(false);
    setPendingId(id);
    setCelebrating(id);
    const pressedAt = performance.now();
    const failed = () => {
      busy.current = false;
      setPendingId(null);
      setCelebrating(null);
      setChooseFailed(true);
    };
    setDefault({
      variables: {
        id
      },
      // `ok: false` is a character that has gone. Navigating on it would
      // bounce straight back here from a guard still reading no character.
      onCompleted: ({
        setDefaultCharacter: result
      }) => {
        if (!mounted.current) return;
        if (!result.ok) {
          failed();
          return;
        }
        leaving.current = window.setTimeout(done, celebrationWait(pressedAt, performance.now(), clock?.reduced() ?? false));
      },
      // No `onError` used to mean a failed write left the learner stuck on
      // this screen with no feedback -- this is the only screen every new
      // account must pass. Reporting it rather than navigating anyway:
      // navigating on a failed write is exactly the loop `done()` moved
      // into `onCompleted` above to prevent, since `RequireCharacter` would
      // re-read `me`, find no `defaultCharacter`, and bounce back here.
      onError: failed
    });
  }, [done, setDefault]);
  return <section className={`flex flex-col gap-6 ${embedded ? '' : 'mx-auto max-w-3xl p-6'}`}>
      <header className="flex flex-col gap-1">
        {embedded ? <h2 className="text-xl font-semibold">{t('character-title')}</h2> : <h1 className="text-2xl font-semibold">{t('character-title')}</h1>}
        <p className="text-base-content/70">{t('character-intro')}</p>
        {chooseFailed && <p role="alert" className="text-error text-sm">
            {t('character-save-failed')}
          </p>}
      </header>

      <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        {data.characters.map((character, i) => {
        const isChosen = chosen === character.id;
        return <li key={character.id} className="contents">
              <div className="card tint lift rise border-2"
          // The only inline style on this screen, and the reason is
          // PRD §9.2: Tailwind cannot see an interpolated class name, so
          // a per-character colour has to arrive as a custom property.
          style={{
            '--tutor-accent': character.accentHex,
            '--rise-delay': `${Math.min(i, 8) * 40}ms`,
            borderColor: isChosen ? character.accentHex : 'transparent'
          } as React.CSSProperties}>
                <div className="card-body items-center gap-2 p-4 text-center">
                  <button type="button" className="btn btn-ghost h-auto min-h-[var(--hit-min)] w-full flex-col gap-1 py-3" aria-pressed={isChosen} aria-busy={choosing && pendingId === character.id} disabled={choosing || celebrating !== null} onClick={() => choose(character.id)}>
                    {/* Inside the choose button, so the face is part of the
                        target rather than a decoration beside it — this is a
                        screen a six-year-old taps, and the face is what they
                        are choosing by. `role="presentation"` on the rig keeps
                        it out of the accessible name, which the text below
                        carries in full. */}
                    <span className="block w-full max-w-[7rem]">
                      <CharacterFace artKey={character.artKey} accentHex={character.accentHex} intensity={character.intensity} expression={expressionForCard(preview, character.id, celebrating)} phase={phase} />
                    </span>
                    <span className="text-lg font-medium">{character.displayName}</span>
                    <span className="text-base-content/60 text-xs">
                      {choosing && pendingId === character.id ? t('common-saving') : paceLabel(character.pace)}
                    </span>
                  </button>

                  <button type="button" className="btn btn-sm btn-outline min-h-(--hit-min) gap-1" aria-label={t('character-hear-name', {
                name: character.displayName
              })} onClick={() => play(character.id)}>
                    {preview.loading === character.id ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : <Volume2 aria-hidden className="h-4 w-4" />}
                    {preview.playing === character.id ? t('character-stop') : t('character-hear')}
                  </button>

                  {/* Not colour alone -- PRD §11. `aria-pressed` carries it for
                      a screen reader and the tick carries it for everyone. */}
                  {isChosen && <span className="text-xs" aria-hidden>
                      <Check className="inline h-4 w-4" /> {t('character-chosen')}
                    </span>}
                </div>
              </div>
            </li>;
      })}
      </ul>
    </section>;
}